import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InlineFormSchemaDialogComponent } from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import { ComposerApiService } from '@services/composer-api.service';
import { FolderApiService } from '@services/folder-api.service';
import { FileUploadService } from '@services/file-upload.service';
import { ToastrService } from '@services/toaster.service';
import { forkJoin, Observable } from 'rxjs';

import { SUBSTITUTION_ALTERNATIVES_TAB } from './composer-grid.models';

@Injectable()
export class ComposerDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly folderApiService = inject(FolderApiService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly composerApiService = inject(ComposerApiService);
  private readonly toastrService = inject(ToastrService);
  private readonly router = inject(Router);

  closeAll(): void {
    this.dialog.closeAll();
  }

  revert(list: string[]): Observable<any> {
    const requests = list.map((rid) => this.folderApiService.restoreItem(rid));
    return forkJoin(requests);
  }

  saveChangesWarningDialog(onConfirm: () => void): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Warning',
        text: 'It looks like you have been editing something. Please save; otherwise, your changes will be lost.',
        cancel_text: 'Cancel',
        delete_text: 'Continue'
      }
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) onConfirm();
    });
    return dialogRef.afterClosed();
  }

  deleteMultiple(
    rids: string[],
    tabName: string | undefined,
    targetRid: string,
    onComplete: () => void
  ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'Confirm',
        header: `Delete${rids.length > 1 ? ' Items?' : ' Item?'}`,
        text: `${rids.length + (rids.length > 1 ? ' Items' : ' Item')} will be deleted. You can still restore the data from the Trash.`,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      const payload = tabName === SUBSTITUTION_ALTERNATIVES_TAB ? targetRid.replace('#', '') : null;

      this.composerApiService.deleteMultipleDataset(rids, payload).subscribe((res) => {
        this.toastrService
          .showWithAction('Deleted Successfully', 'Undo', 5000)
          .afterDismissed()
          .subscribe((restore) => {
            if (!restore.dismissedByAction) return;
            const trashIds = Object.values(res)
              .filter((e: any) => e.message === 'success')
              .map((e: any) => e.trash_rids[0]);
            this.revert(trashIds).subscribe(() => onComplete());
          });
        onComplete();
      });
    });
  }

  deleteSingle(
    rowData: any,
    tabName: string | undefined,
    targetRid: string,
    inlineEditRow: string[],
    onEditRowCleanup: (updated: string[]) => void,
    onComplete: () => void
  ): void {
    const lines = rowData.row;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'Confirm',
        header: 'Delete Item?',
        text: 'Item will be deleted. You can still restore the data from the Trash',
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;

      let rid = '';
      lines.forEach((row: any) => {
        if (row.key.endsWith('.rid') || row.key.endsWith('$rid')) {
          rid = row.rid;
        }
      });
      if (!rid) return;

      const payload = tabName === SUBSTITUTION_ALTERNATIVES_TAB
        ? { rid: targetRid.replace('#', '') }
        : null;

      this.composerApiService.deleteData(rid, payload).subscribe((res) => {
        const trash = res[`#${rid}`];
        const trashIds = trash.trash_rids;

        if (inlineEditRow.includes(rid)) {
          onEditRowCleanup(inlineEditRow.filter((e) => e !== rid));
        }

        if (trash.message === 'success') {
          this.toastrService
            .showWithAction('Deleted Successfully', 'Undo', 5000)
            .afterDismissed()
            .subscribe((restore) => {
              if (!restore.dismissedByAction) return;
              this.folderApiService.restoreItem(trashIds[0]).subscribe(() => {
                this.toastrService.showMessage('Restored successfully.');
                onComplete();
              });
            });
        }
        onComplete();
      });
    });
  }

  openEditDialog(
    formSchema: any[],
    lines: any[],
    targetRid: string,
    layer: string | undefined,
    onComplete: () => void
  ): void {
    const schema = {
      formSchema,
      lines: lines || [],
      rid: targetRid,
      layer,
      disableSubmit: true
    };

    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      width: '50vw',
      data: schema,
      autoFocus: false
    });
    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data?.value?.Inputs) {
        const payload = data.updatedValues || data.value.Inputs;
        this.processSubmitPayload(payload, formSchema, lines, targetRid, dialogRef, onComplete);
      }
    });

    instance.changeDataEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data?.valid && data?.updatedValues) {
        dialogRef.componentInstance.data.disableSubmit = false;
      }
    });
  }

  navigateToEdit(lines: any[]): void {
    for (const row of lines) {
      let key = row.key.replace('$', '.');
      key = key.split('.').pop();
      if (['in', 'out', 'name'].includes(key)) {
        this.router.navigate(['/mattermaps/overview/', row.rid, 'edit']);
      }
    }
  }

  private processSubmitPayload(
    payload: any,
    formSchema: any[],
    lines: any[],
    targetRid: string,
    dialogRef: MatDialogRef<InlineFormSchemaDialogComponent>,
    onComplete: () => void
  ): void {
    const addNestedKeysToPayload = (keys: string[], value: any) => {
      let current = payload;
      keys.forEach((key) => {
        if (!(key in current)) {
          current[key] = value;
        }
        current = current[key];
      });
    };

    const regex = /^#\d+:\d+$/;
    const findRids = (obj: any, parents: string[] = []) => {
      Object.keys(obj).forEach((k) => {
        const val = obj[k];
        const allParents = [...parents, k];
        if (val !== null && typeof val === 'object') {
          findRids(val, allParents);
        } else if (typeof val === 'string' && regex.test(val)) {
          addNestedKeysToPayload(allParents, val);
        }
      });
    };
    findRids(payload);

    let isFileUpload = false;
    let uploaderCount = 0;

    formSchema.forEach((e) => {
      if (!['attachments', 'image'].includes(e.formtype)) return;
      const arrKey = e.key.split('.');
      const key = arrKey.pop();
      if (payload[arrKey[0]][key]?.[0]) {
        isFileUpload = true;
        uploaderCount++;
        this.fileUploadService.newFileUpload(targetRid, payload[arrKey[0]][key][0]).subscribe({
          next: (response) => {
            payload[arrKey[0]][key] = Object.values(response)[0].file_rid;
            uploaderCount--;
            if (uploaderCount === 0) this.saveGridLine(lines, payload, targetRid, dialogRef, onComplete);
          },
          error: () => {
            uploaderCount--;
            if (uploaderCount === 0) this.saveGridLine(lines, payload, targetRid, dialogRef, onComplete);
          }
        });
      } else if (payload[arrKey[0]][key] && !payload[arrKey[0]][key][0]) {
        payload[arrKey[0]][key] = payload[arrKey[0]][key][0]?.rid;
      }
    });

    if (!isFileUpload) {
      this.saveGridLine(lines, payload, targetRid, dialogRef, onComplete);
    }
  }

  private saveGridLine(
    lines: any[],
    payload: any,
    targetRid: string,
    dialogRef: MatDialogRef<InlineFormSchemaDialogComponent>,
    onComplete: () => void
  ): void {
    dialogRef.componentInstance.data.disableSubmit = true;

    let rid: string | undefined;
    lines.forEach((row) => {
      if (row.key.endsWith('.rid') || row.key.endsWith('$rid')) {
        rid = row.rid;
      }
    });
    const data: any = Object.values(payload)[0];
    if (rid) {
      data.rid = rid;
    }

    this.composerApiService.submitEditedMetadata(targetRid, payload).subscribe((res: any) => {
      if (res.updated) {
        dialogRef?.close();
        onComplete();
      } else if (res?.fails?.length) {
        dialogRef.componentInstance.data.disableSubmit = false;
        this.toastrService.showMessage((res?.fails || []).map((f) => f.message) ?? 'Something went wrong');
      }
    });
  }
}
